import { venueZone } from "../format.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cleanPrefs,
  FLAVOUR_GUEST_LABELS,
  HOLD_MINUTES,
  lastSeenLabel,
  MAX_PARTY_SIZE,
  normaliseMobile,
  rankSpecsForTaste,
  type FlavourFamilyKey,
  type PartyMessage,
  type PartyStatus,
} from "@baros/core";
import { useApp } from "../context.js";
import { NoVenue } from "../NoVenue.js";
import { queueApi, type DoorGuest, type QueueParty } from "./api.js";
import "./guests.css";

/** How often the list refreshes itself, so two people on the door see the same queue. */
const POLL_MS = 15_000;

function minutesSince(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
}

/** A span of time: `4 min`, `1 h 12 min`, or `under a minute`. */
function ago(iso: string, now: number): string {
  const m = minutesSince(iso, now);
  if (m < 1) return "under a minute";
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { timeZone: venueZone(), day: "numeric", month: "short" });
}

const STATUS_LABEL: Readonly<Record<PartyStatus, string>> = {
  waiting: "Waiting",
  ready: "Table ready",
  seated: "Seated",
  cancelled: "Cancelled",
  "no-show": "No-show",
};

/**
 * The latest text and what became of it, in words the door can act on. With
 * texting not set up at all, the banner says so once and each party stays
 * quiet about it.
 */
function lastText(messages: readonly PartyMessage[], texting: boolean): { cls: string; text: string } | null {
  const m = messages[messages.length - 1];
  if (!m || (!texting && m.status === "skipped")) return null;
  const what = m.kind === "joined" ? "Queue text" : "Table text";
  if (m.status === "sent") return { cls: "ok", text: `${what} sent` };
  return { cls: m.status === "failed" ? "err" : "warn", text: m.detail ?? `${what} not sent` };
}

/**
 * The door queue (#126): who is waiting for a table, and the two texts.
 *
 * Built for one thumb on a tablet behind a bar mid-service: the number
 * first (it finds a returning guest), big buttons, and every move one tap.
 * Nothing moves on its own — "table ready" starts a fifteen-minute count
 * here on the screen, and whoever is on the door decides what happens when
 * it runs out.
 */
export function QueueScreen() {
  const app = useApp();
  const venueId = app.venueId;
  const [parties, setParties] = useState<QueueParty[] | null>(null);
  const [texting, setTexting] = useState(true);
  const [textingOff, setTextingOff] = useState<"plan" | "setup" | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string; undo?: () => void } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [showEarlier, setShowEarlier] = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    try {
      const res = await queueApi.list(app.token, venueId);
      setParties(res.parties);
      setTexting(res.texting);
      setTextingOff(res.textingOff ?? (res.texting ? null : "setup"));
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not read the queue" });
    }
  }, [app.token, venueId]);

  useEffect(() => {
    void load();
    const poll = window.setInterval(() => void load(), POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [load]);

  if (!venueId) return <NoVenue what="a door queue" />;

  const open = (parties ?? []).filter((p) => p.status === "waiting" || p.status === "ready");
  const earlier = (parties ?? []).filter((p) => p.status !== "waiting" && p.status !== "ready").reverse();
  const waitingCount = open.filter((p) => p.status === "waiting").length;

  async function move(p: QueueParty, to: PartyStatus) {
    setBusy(p.partyId);
    setMsg(null);
    try {
      const res = await queueApi.move(app.token, venueId!, p.partyId, to);
      const m = res.party.messages[res.party.messages.length - 1];
      if (to === "ready" && m && m.status !== "sent" && texting) setMsg({ kind: "err", text: `${p.name}: ${m.detail ?? "not texted"}` });
      if (to === "ready" && !texting) setMsg({ kind: "ok", text: `Tell ${p.name} their table's ready.` });
      if (to === "seated" || to === "cancelled" || to === "no-show") {
        setMsg({
          kind: "ok",
          text: `${p.name} — ${STATUS_LABEL[to].toLowerCase()}.`,
          ...(to !== "seated" ? { undo: () => void move({ ...p, status: to }, "waiting") } : {}),
        });
      }
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "That did not work" });
    } finally {
      setBusy(null);
      await load();
    }
  }

  async function textAgain(p: QueueParty) {
    setBusy(p.partyId);
    try {
      const res = await queueApi.textAgain(app.token, venueId!, p.partyId);
      const m = res.party.messages[res.party.messages.length - 1];
      setMsg(m?.status === "sent" ? { kind: "ok", text: `Texted ${p.name} again.` } : { kind: "err", text: m?.detail ?? "Not texted" });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "That did not work" });
    } finally {
      setBusy(null);
      await load();
    }
  }

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {parties === null ? "Queue" : waitingCount === 0 ? "Nobody waiting" : `${waitingCount} waiting`}
          </div>
        </div>
      </div>

      {!texting && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className="msg warn">
            {textingOff === "plan"
              ? "Texting isn't part of your Bar OS, so guests aren't texted — tell them in person when their table is ready."
              : "Texting isn't set up yet. The queue works, but guests aren't texted — tell them in person when their table is ready."}
          </div>
        </div>
      )}
      {msg && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className={`msg ${msg.kind === "err" ? "err" : "ok"} row`} style={{ gap: 10 }}>
            <span>{msg.text}</span>
            {msg.undo && (
              <>
                <span className="spacer" />
                <button
                  className="btn sm"
                  onClick={() => {
                    const undo = msg.undo;
                    setMsg(null);
                    undo?.();
                  }}
                >
                  Undo
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="split q-split">
        <section className="pane" style={{ padding: "0 20px 20px" }}>
          <AddParty
            onAdded={(p) => {
              void load();
              setMsg(addedMessage(p, texting));
            }}
            onMessage={setMsg}
          />
        </section>

        <section className="pane q-list" style={{ padding: "0 20px 20px" }}>
          {parties === null && <div className="label">Reading the queue…</div>}
          {parties !== null && open.length === 0 && (
            <div className="q-empty">Nobody in the queue. Add a party when the bar is full.</div>
          )}
          {open.map((p, i) => {
            const text = lastText(p.messages, texting);
            const heldFor = p.readyAt ? minutesSince(p.readyAt, now) : 0;
            const over = p.status === "ready" && heldFor >= HOLD_MINUTES;
            return (
              <div key={p.partyId} className={`q-party${p.status === "ready" ? " ready" : ""}${over ? " over" : ""}`}>
                <div className="q-party-main">
                  <div className="q-pos num">{p.status === "waiting" ? i + 1 : "✓"}</div>
                  <div className="q-who">
                    <div className="q-name">
                      {p.name} <span className="muted">· {p.size}</span>
                    </div>
                    <div className="q-meta">
                      {p.status === "waiting" && <>Waiting {ago(p.joinedAt, now)}</>}
                      {p.status === "ready" && p.readyAt && (
                        <>
                          Table ready for {ago(p.readyAt, now)} ·{" "}
                          {over ? <strong>{HOLD_MINUTES} minutes are up</strong> : `${HOLD_MINUTES - heldFor} min left`}
                        </>
                      )}
                      {p.guest && p.guest.visits > 0 && <> · back again ({p.guest.visits} before)</>}
                      {p.note && <> · {p.note}</>}
                    </div>
                    {text && <div className={`q-text ${text.cls}`}>{text.text}</div>}
                  </div>
                </div>
                <div className="q-actions">
                  {p.status === "waiting" && (
                    <button className="btn primary" disabled={busy !== null} onClick={() => void move(p, "ready")}>
                      Table ready
                    </button>
                  )}
                  <button className="btn" disabled={busy !== null} onClick={() => void move(p, "seated")}>
                    Seated
                  </button>
                  {p.status === "ready" && texting && (
                    <button className="btn" disabled={busy !== null} onClick={() => void textAgain(p)}>
                      Text again
                    </button>
                  )}
                  <button className="btn" disabled={busy !== null} onClick={() => void move(p, p.status === "ready" ? "no-show" : "cancelled")}>
                    {p.status === "ready" ? "No-show" : "Cancel"}
                  </button>
                </div>
              </div>
            );
          })}

          {earlier.length > 0 && (
            <div className="q-earlier">
              <button className="btn sm" onClick={() => setShowEarlier(!showEarlier)} aria-expanded={showEarlier}>
                Earlier tonight · {earlier.length}
              </button>
              {showEarlier &&
                earlier.map((p) => (
                  <div key={p.partyId} className="q-closed row">
                    <span>
                      {p.name} <span className="muted">· {p.size}</span>
                    </span>
                    <span className="spacer" />
                    <span className="label">
                      {STATUS_LABEL[p.status]}
                      {p.closedAt ? ` · ${ago(p.closedAt, now)} ago` : ""}
                    </span>
                    {(p.status === "cancelled" || p.status === "no-show") && (
                      <button className="btn sm" disabled={busy !== null} onClick={() => void move(p, "waiting")}>
                        Back in queue
                      </button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function addedMessage(p: QueueParty, texting: boolean): { kind: "ok" | "err"; text: string } {
  const m = p.messages[0];
  if (!texting) return { kind: "ok", text: `${p.name} is on the list.` };
  if (!m || m.status === "sent") return { kind: "ok", text: `${p.name} is on the list and has been texted.` };
  return { kind: "err", text: `${p.name} is on the list. ${m.detail ?? "They were not texted."}` };
}

/**
 * Adding a party: the number first, because it is what finds a returning
 * guest — their name, what they like, and what on tonight's list fits.
 */
function AddParty(props: {
  readonly onAdded: (p: QueueParty) => void;
  readonly onMessage: (m: { kind: "ok" | "err"; text: string; undo?: () => void } | null) => void;
}) {
  const app = useApp();
  const venueId = app.venueId!;
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [size, setSize] = useState(2);
  const [note, setNote] = useState("");
  const [guest, setGuest] = useState<DoorGuest | null>(null);
  const [busy, setBusy] = useState(false);
  const looked = useRef<string | null>(null);
  const e164 = normaliseMobile(phone);

  // Look the number up once it is a whole mobile.
  useEffect(() => {
    if (!e164) {
      setGuest(null);
      looked.current = null;
      return;
    }
    if (looked.current === e164) return;
    looked.current = e164;
    let live = true;
    queueApi
      .lookup(app.token, venueId, e164)
      .then((res) => {
        if (!live) return;
        setGuest(res.guest);
        if (res.guest) setName((n) => n || res.guest!.name);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [e164, app.token, venueId]);

  // Tonight's drinks that fit what a returning guest has said they like.
  const suggestions = useMemo(() => {
    if (!guest || guest.prefs.aromas.length === 0) return [];
    const pourable = app.library.specs.filter((s) => {
      const o = app.overrides.get(s.specId);
      return s.status === "available" && o && o.available !== false && o.price !== null;
    });
    const ingredients = new Map(app.library.ingredients.map((i) => [i.ingredientId, i]));
    return rankSpecsForTaste(pourable, ingredients, { families: guest.prefs.aromas, styles: guest.prefs.styles })
      .slice(0, 3)
      .map((m) => m.spec.name);
  }, [guest, app.library, app.overrides]);

  const ready = e164 !== null && name.trim().length > 0 && size >= 1;

  function reset() {
    setPhone("");
    setName("");
    setSize(2);
    setNote("");
    setGuest(null);
    looked.current = null;
  }

  async function add() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const res = await queueApi.add(app.token, venueId, { phone, name: name.trim(), size, note: note.trim() });
      reset();
      props.onAdded(res.party);
    } catch (err) {
      props.onMessage({ kind: "err", text: err instanceof Error ? err.message : "Could not add the party" });
    } finally {
      setBusy(false);
    }
  }

  async function logVisit() {
    if (!guest) return;
    setBusy(true);
    try {
      const res = await queueApi.logVisit(app.token, venueId, guest.guestId);
      const who = guest.name;
      const at = res.visit.at;
      const id = guest.guestId;
      reset();
      props.onMessage({
        kind: "ok",
        text: `Logged ${who} as in tonight.`,
        undo: () => void queueApi.undoVisit(app.token, venueId, id, at).catch(() => undefined),
      });
    } catch (err) {
      props.onMessage({ kind: "err", text: err instanceof Error ? err.message : "Could not log the visit" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="q-add"
      onSubmit={(e) => {
        e.preventDefault();
        void add();
      }}
    >
      <div className="w-step">Add a party</div>
      <label className="field">
        <span>Mobile</span>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="04xx xxx xxx"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-invalid={phone.replace(/\D/g, "").length >= 10 && !e164}
        />
      </label>
      {phone.replace(/\D/g, "").length >= 10 && !e164 && (
        <div className="label warn">That isn't an Australian mobile — the queue can only text a mobile.</div>
      )}

      {guest && (
        <div className="q-guest">
          <div className="row">
            <strong>{guest.name}</strong>
            <span className="spacer" />
            <span className="label">
              {lastSeenLabel(guest.lastSeenSource)} {day(guest.lastSeenAt)} · {guest.visits} visit{guest.visits === 1 ? "" : "s"}
            </span>
          </div>
          {(guest.prefs.aromas.length > 0 || guest.prefs.styles.length > 0) && (
            <div className="q-prefs">Likes {[...cleanPrefs(guest.prefs).aromas.map((f) => FLAVOUR_GUEST_LABELS[f as FlavourFamilyKey]), ...guest.prefs.styles].join(", ")}</div>
          )}
          {suggestions.length > 0 && <div className="q-prefs">Tonight: {suggestions.join(", ")}</div>}
          <button className="btn sm" type="button" disabled={busy} onClick={() => void logVisit()}>
            In tonight without queuing? Log a visit
          </button>
        </div>
      )}

      <label className="field">
        <span>Name</span>
        <input type="text" autoComplete="off" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </label>

      <div className="field">
        <span>How many</span>
        <div className="q-sizes">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <button key={n} type="button" className="btn" aria-pressed={size === n} onClick={() => setSize(n)}>
              {n}
            </button>
          ))}
          <input
            type="number"
            className="num-input"
            aria-label="Party size"
            min={1}
            max={MAX_PARTY_SIZE}
            value={size}
            onChange={(e) => setSize(Math.max(1, Math.min(MAX_PARTY_SIZE, Math.round(Number(e.target.value) || 1))))}
          />
        </div>
      </div>

      <label className="field">
        <span>Note — optional</span>
        <input type="text" placeholder="Booth, high chair, birthday…" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </label>

      <button className="btn primary q-submit" type="submit" disabled={!ready || busy}>
        {busy ? "Adding…" : "Add to queue"}
      </button>
    </form>
  );
}
